/**
 * @file inward-report.ts
 * @module app/modules/inward/utils/inward-report
 * @description End-of-day Inward report (rows come from GET _product/inward/report): table
 *   cells in the warehouse "Inbound ... Report" layout plus who did what and when, inward
 *   start / done / duration, and pending actions (with whom, for how long). Also a PNG of the
 *   same table drawn on a canvas (yellow title, blue header, yellow total row).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

export type ReportValue = string | number;

export interface InwardReportPending {
  action: string;
  with: string;
  since: string;
  duration: string;
}

export interface InwardReportRow {
  grnRefKey: string;
  /** one value per header, same order as `headers` */
  values: ReportValue[];
  pending?: InwardReportPending[];
  /** GRN id and the manual "EFD verified" tick (who / when) */
  grnId?: string;
  efd?: string;
  efdVerified?: boolean;
  efdVerifiedBy?: string;
  efdVerifiedAt?: string;
}

export interface InwardReport {
  title: string;
  date: string;
  warehouseName: string;
  headers: string[];
  rows: InwardReportRow[];
  total: {
    rows: number;
    tons: number;
    skuCount: number;
    qtyReceived: number;
    invoiceAmount: number;
  };
  /** total row, one value per header */
  totalValues: ReportValue[];
  generatedAt?: string;
  /** this user may tick "EFD verified" (Inward right 76) */
  canVerifyEfd?: boolean;
}

/** Column of the EFD status (-1 when the report has none). */
export const efdColumn = (report: Pick<InwardReport, "headers">) =>
  report.headers.indexOf("EFD");

/** The EFD cell text after a tick / untick (same wording as the server and the Excel). */
export const efdCellText = (
  row: Pick<
    InwardReportRow,
    "efdVerified" | "efdVerifiedBy" | "efdVerifiedAt" | "efd"
  >
) =>
  row.efdVerified
    ? ["Verified", row.efdVerifiedBy, row.efdVerifiedAt]
        .filter(Boolean)
        .join(" · ")
    : row.efd && row.efd !== "Verified"
    ? row.efd
    : "Pending";

/** Column index of "Reported ton" (the total row shows it with one decimal). */
const TON_COLUMN = 3;

const formatValue = (v: ReportValue | undefined): string =>
  typeof v === "number"
    ? v.toLocaleString("en-US", { maximumFractionDigits: 2 })
    : v ?? "";

/** Body cells (text, may contain line breaks), one per header. */
export const reportRowCells = (
  report: InwardReport,
  row: InwardReportRow
): string[] => report.headers.map((_, i) => formatValue(row.values?.[i]));

/** Total row cells, one per header. */
export const reportTotalCells = (report: InwardReport): string[] =>
  report.headers.map((_, i) => {
    const v = report.totalValues?.[i];
    if (i === TON_COLUMN && typeof v === "number") return `${v.toFixed(1)} TON`;
    return formatValue(v);
  });

export const REPORT_COLORS = {
  title: "#FFFF00",
  header: "#5B9BD5",
  total: "#FFFF00",
  border: "#000000",
  text: "#000000",
  body: "#FFFFFF",
};

/** Column widths (px) for the PNG; wide ones hold the who / when / pending text. */
const WIDE: Record<string, number> = {
  "Vendor Name": 220,
  "Loading bay manager / Dock": 190,
  "Warehouse approval": 210,
  "Finance approval": 250,
  "Actions (who · when)": 420,
  "Pending actions": 300,
  "Pending with": 280,
};
export const columnWidth = (header: string) => WIDE[header] ?? 120;

const LINE_HEIGHT = 18;
const PADDING = 8;
const MIN_ROW = 34;

/** Splits text into lines that fit `maxWidth` (keeps explicit line breaks). */
export const wrapText = (
  ctx: Pick<CanvasRenderingContext2D, "measureText">,
  text: string,
  maxWidth: number
): string[] => {
  const out: string[] = [];
  String(text ?? "")
    .split("\n")
    .forEach((para) => {
      const words = para.split(" ");
      let line = "";
      words.forEach((word) => {
        const next = line ? `${line} ${word}` : word;
        if (ctx.measureText(next).width <= maxWidth || !line) {
          line = next;
        } else {
          out.push(line);
          line = word;
        }
      });
      out.push(line);
    });
  return out;
};

const rowHeight = (lineCounts: number[]) =>
  Math.max(MIN_ROW, Math.max(...lineCounts, 1) * LINE_HEIGHT + PADDING * 2);

/** Draws the report onto a canvas 2D context; returns the height used. */
export const drawReport = (
  ctx: CanvasRenderingContext2D,
  report: InwardReport,
  measureOnly = false
): { width: number; height: number } => {
  const widths = report.headers.map(columnWidth);
  const width = widths.reduce((s, w) => s + w, 0);
  ctx.textBaseline = "top";
  ctx.strokeStyle = REPORT_COLORS.border;
  ctx.lineWidth = 1;
  let y = 0;

  const drawRow = (
    cells: string[],
    fill: string,
    bold: boolean,
    leftAlign: boolean
  ) => {
    ctx.font = `${bold ? "bold " : ""}13px Calibri, Arial, sans-serif`;
    const wrapped = cells.map((c, i) =>
      wrapText(ctx, c, widths[i] - PADDING * 2)
    );
    const h = rowHeight(wrapped.map((w) => w.length));
    if (!measureOnly) {
      ctx.fillStyle = fill;
      ctx.fillRect(0, y, width, h);
      let x = 0;
      wrapped.forEach((lines, i) => {
        ctx.strokeRect(x, y, widths[i], h);
        ctx.fillStyle = REPORT_COLORS.text;
        ctx.textAlign = leftAlign ? "left" : "center";
        const tx = leftAlign ? x + PADDING : x + widths[i] / 2;
        lines.forEach((line, n) =>
          ctx.fillText(line, tx, y + PADDING + n * LINE_HEIGHT)
        );
        x += widths[i];
      });
    }
    y += h;
  };

  // Title (merged across all columns)
  if (!measureOnly) {
    ctx.fillStyle = REPORT_COLORS.title;
    ctx.fillRect(0, 0, width, MIN_ROW);
    ctx.strokeRect(0, 0, width, MIN_ROW);
    ctx.font = "bold 17px Calibri, Arial, sans-serif";
    ctx.fillStyle = REPORT_COLORS.text;
    ctx.textAlign = "left";
    ctx.fillText(report.title, PADDING, PADDING);
  }
  y = MIN_ROW;

  drawRow(report.headers, REPORT_COLORS.header, true, false);
  report.rows.forEach((r) =>
    drawRow(reportRowCells(report, r), REPORT_COLORS.body, false, true)
  );
  drawRow(reportTotalCells(report), REPORT_COLORS.total, true, false);
  return { width, height: y };
};

/** Renders the report as a PNG blob (null if the browser has no canvas). */
export const reportToPng = (report: InwardReport): Promise<Blob | null> => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  const { width, height } = drawReport(ctx, report, true);
  const scale = 2; // sharp on tablets
  canvas.width = width * scale;
  canvas.height = height * scale;
  ctx.scale(scale, scale);
  drawReport(ctx, report);
  return new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/png")
  );
};

/** Starts a browser download of a blob. */
export const saveBlob = (blob: Blob, fileName: string) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};
